import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Send,
  User,
  Loader2,
  Copy,
  Check,
  MessageSquare,
  Settings,
  Plus,
  Search,
  Download,
  History,
  ThumbsUp,
  ThumbsDown,
  Sparkles,
  RotateCcw,
  BarChart3,
  Trash2,
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { getKaiChatConfig } from '../../constants/kaiChatConfig';
import { createKaiCopilotService } from '../../services/kaiCopilotService';
import { readOrganizationIdFromContext } from '../../utils/organizationContext';

// Series color matches the existing "Denial Rate" line already used on the
// Analytics page (TemporalTrends.jsx) — one hue, kept consistent across the app
// rather than introducing a second convention for the same metric.
const TREND_LINE_COLOR = '#8b5cf6';

/**
 * Compact inline trend chart for chat responses that carry a `chart` payload
 * (see conversational_workflows.py's _handle_trends). Single series, so no
 * legend box — the title above the chart names what's plotted.
 */
const ChatTrendChart = ({ chart }) => {
  if (!chart?.data?.length) return null;
  const unit = chart.unit || '';

  return (
    <div className="mt-3 p-3 bg-white border border-gray-200 rounded-lg">
      <div className="text-sm font-medium text-gray-700 mb-2">{chart.title}</div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chart.data} margin={{ top: 8, right: 12, bottom: 0, left: -12 }}>
          <CartesianGrid vertical={false} stroke="#e1e0d9" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#898781' }}
            angle={-35}
            textAnchor="end"
            height={45}
            axisLine={{ stroke: '#c3c2b7' }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#898781' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${v}${unit}`}
            width={40}
          />
          <Tooltip
            formatter={(value) => [`${value}${unit}`, chart.metric === 'denial_rate' ? 'Denial Rate' : chart.metric]}
            labelStyle={{ color: '#0b0b0b' }}
            contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e1e0d9' }}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={TREND_LINE_COLOR}
            strokeWidth={2}
            dot={{ r: 4, strokeWidth: 2, stroke: '#fff', fill: TREND_LINE_COLOR }}
            activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
          />
        </LineChart>
      </ResponsiveContainer>
      {chart.link && (
        <Link to={chart.link} className="mt-2 inline-block text-xs text-blue-600 underline hover:text-blue-800">
          {chart.linkLabel || 'View details'}
        </Link>
      )}
    </div>
  );
};

/**
 * Unified Kai chat UI — Cursor/Claude/OpenAI-style interface for every RCM service.
 *
 * @param {string} serviceKey - key in kaiChatConfig (e.g. 'denial_management')
 * @param {object} [configOverride] - optional overrides for welcomeMessage, modelLabel, etc.
 * @param {object} [copilotService] - optional injected API client (for DenialManagement full agent)
 */
const ModernChatInterface = ({ serviceKey, configOverride = {}, copilotService: copilotServiceProp }) => {
  const config = { ...getKaiChatConfig(serviceKey), ...configOverride };
  const copilotService = useMemo(
    () =>
      copilotServiceProp ||
      createKaiCopilotService({
        backendPort: config.backendPort,
        frontendPort: config.frontendPort,
      }),
    [copilotServiceProp, config.backendPort, config.frontendPort]
  );
  // Conversation CRUD always uses the shared client, so it stays in step as
  // endpoints are added — only the ORIGIN is borrowed from an injected service.
  // Resolving the origin here yields same-origin /api/v1, correct on a service
  // subdomain but wrong in the shell, where /api proxies to the SaaS backend
  // (no /ai-copilot routes → 404). An injected service publishes the origin it
  // already resolved (e.g. /rcm-api/<svc>); undefined falls back to same-origin.
  const conversationService = useMemo(
    () =>
      createKaiCopilotService({
        apiBaseUrl: copilotServiceProp?.apiBaseUrl,
        backendPort: config.backendPort,
        frontendPort: config.frontendPort,
      }),
    [copilotServiceProp, config.backendPort, config.frontendPort]
  );

  const navigate = useNavigate();
  const welcomeMessage = config.welcomeMessage;

  const welcomeMessages = useMemo(() => [
    {
      id: 'welcome',
      role: 'assistant',
      content: welcomeMessage,
      timestamp: new Date(),
      isStreaming: false,
    },
  ], [welcomeMessage]);

  const [messages, setMessages] = useState(welcomeMessages);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [messageFeedback, setMessageFeedback] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [selectedModel, setSelectedModel] = useState(config.modelLabel);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const userId = useMemo(() => {
    // Extract user ID from JWT token (set during login / shell auth handoff)
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.sub) return payload.sub;
      } catch { /* malformed token — fall through */ }
    }
    // Fallback: check user_data object stored by SaaS auth
    const userData = localStorage.getItem('user_data');
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        if (parsed.id) return String(parsed.id);
      } catch { /* ignore */ }
    }
    return null;
  }, []);

  const organizationId = useMemo(() => readOrganizationIdFromContext(), []);

  // Load conversation list on mount
  useEffect(() => {
    if (!userId || !organizationId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setConversationsLoading(true);
        const data = await conversationService.listConversations(userId, organizationId);
        if (!cancelled) setConversations(data.conversations || []);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error('Failed to load conversations:', err);
      } finally {
        if (!cancelled) setConversationsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [conversationService, userId, organizationId]);

  // Load a conversation's messages when sidebar item is clicked
  const loadConversation = async (convId) => {
    if (!userId || !organizationId || convId === activeConversationId) return;
    try {
      const data = await conversationService.getConversation(convId, userId, organizationId);
      const loaded = (data.messages || []).map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        timestamp: new Date(m.created_at),
        isStreaming: false,
        metadata: m.metadata,
      }));
      setMessages(loaded.length > 0 ? loaded : welcomeMessages);
      setActiveConversationId(convId);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to load conversation:', err);
    }
  };

  const deleteConversation = async (convId, e) => {
    if (e) e.stopPropagation();
    if (!userId || !organizationId) return;
    try {
      await conversationService.deleteConversation(convId, userId, organizationId);
      setConversations((prev) => prev.filter((c) => c.id !== convId));
      if (activeConversationId === convId) {
        setActiveConversationId(null);
        setMessages(welcomeMessages);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to delete conversation:', err);
    }
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [inputMessage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!copilotService?.listConversations) return undefined;
    let cancelled = false;
    const organizationId = readOrganizationIdFromContext();
    (async () => {
      try {
        const payload = await copilotService.listConversations(userId, organizationId);
        if (!cancelled) {
          setConversations(payload?.conversations || []);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[Kai] conversation history unavailable:', error?.message || error);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [copilotService, userId]);

  const buildContext = () => {
    const organizationId = readOrganizationIdFromContext();
    const urlParams = new URLSearchParams(window.location.search);
    const context = {
      domain: config.serviceKey,
      ...(organizationId && { organization_id: organizationId, organizationId }),
    };
    (config.contextKeys || []).forEach((key) => {
      const val = urlParams.get(key);
      if (val) context[key] = val;
    });
    return context;
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const currentInput = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);

    const streamingMessage = {
      id: Date.now() + 1,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };
    setMessages((prev) => [...prev, streamingMessage]);

    try {
      const response = await copilotService.chat(
        currentInput,
        userId,
        buildContext(),
        activeConversationId
      );

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === streamingMessage.id
            ? {
                ...msg,
                content:
                  response.response ||
                  "I apologize, but I couldn't process your request. Please try again.",
                isStreaming: false,
                conversationId: response.conversation_id,
                metadata: {
                  model: selectedModel,
                  processingTime: response.metadata?.processing_time || response.metadata?.processingTime || '—',
                  tokens: response.metadata?.tokens,
                  confidence: response.metadata?.confidence,
                  chart: response.metadata?.chart,
                },
              }
            : msg
        )
      );

      if (response.conversation_id) {
        const isNew = !activeConversationId;
        setActiveConversationId(response.conversation_id);
        if (isNew && userId && organizationId) {
          // Refresh sidebar to show the new conversation
          conversationService.listConversations(userId, organizationId)
            .then((data) => setConversations(data.conversations || []))
            .catch(() => {});
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error sending message:', error);
      }
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === streamingMessage.id
            ? {
                ...msg,
                content:
                  error.message?.length < 200
                    ? error.message
                    : "I'm sorry, I encountered an error while processing your request. Please try again.",
                isStreaming: false,
                error: true,
              }
            : msg
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Falls back to execCommand when the Async Clipboard API is unavailable —
  // cross-origin iframe embeds are denied clipboard-write unless the host
  // page delegates it via the iframe allow attribute.
  const copyWithExecCommand = (content) => {
    const textarea = document.createElement('textarea');
    textarea.value = content;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    try {
      textarea.select();
      return document.execCommand('copy');
    } catch (error) {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const copyMessage = async (messageId, content) => {
    try {
      await navigator.clipboard.writeText(content);
    } catch (error) {
      if (!copyWithExecCommand(content)) {
        console.error('Failed to copy message:', error);
        return;
      }
    }
    setCopiedMessageId(messageId);
    setTimeout(() => setCopiedMessageId(null), 2000);
  };

  const handleFeedback = (messageId, feedback) => {
    setMessageFeedback((prev) => ({ ...prev, [messageId]: feedback }));
  };

  const regenerateResponse = async (messageId) => {
    const messageIndex = messages.findIndex((msg) => msg.id === messageId);
    if (messageIndex > 0) {
      const userMessage = messages[messageIndex - 1];
      if (userMessage.role === 'user') {
        setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
        setInputMessage(userMessage.content);
        setTimeout(() => handleSendMessage(), 100);
      }
    }
  };

  const clearConversation = () => {
    setMessages(welcomeMessages);
    setActiveConversationId(null);
  };

  const exportConversation = () => {
    const conversationText = messages
      .filter((msg) => !msg.isStreaming)
      .map((msg) => `${msg.role === 'user' ? 'You' : 'Kai'}: ${msg.content}`)
      .join('\n\n');

    const blob = new Blob([conversationText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kai-${config.serviceKey}-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderInlineMarkdown = (text) => {
    // Order matters: `**bold**` must precede any single-marker italic pattern
    // so `**bold**` isn't split into `*` + `*bold*` + `*`. `_[^_\n]+_` is
    // deliberately conservative — inline text only (no newlines), and the
    // outer `_`s can't be re-used, so `_a_ _b_` still splits into two spans.
    const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\)|_[^_\n]+_)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('`') && part.endsWith('`')) {
        return <code key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-800 rounded text-xs font-mono">{part.slice(1, -1)}</code>;
      }
      if (part.length > 2 && part.startsWith('_') && part.endsWith('_')) {
        return <em key={i} className="italic text-gray-600">{part.slice(1, -1)}</em>;
      }
      const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (linkMatch) {
        const [, label, url] = linkMatch;
        const linkClassName = 'text-blue-600 underline hover:text-blue-800';
        if (url.startsWith('/')) {
          // Same-app SPA route — navigate() (already used elsewhere in this
          // component) instead of a full page reload.
          return (
            <a
              key={i}
              href={url}
              className={linkClassName}
              onClick={(e) => {
                e.preventDefault();
                navigate(url);
              }}
            >
              {label}
            </a>
          );
        }
        return (
          <a key={i} href={url} target="_blank" rel="noopener noreferrer" className={linkClassName}>
            {label}
          </a>
        );
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  };

  const formatMessage = (content) => {
    const lines = content.split('\n');
    return lines.map((line, index) => {
      if (line.trim().startsWith('```')) {
        return <div key={index} className="my-2" />;
      }
      if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
        const bulletText = line.replace(/^\s*[-•]\s*/, '');
        return (
          <div key={index} className="ml-4 mb-1 flex">
            <span className="text-gray-400 mr-2">•</span>
            <span className="text-gray-700">{renderInlineMarkdown(bulletText)}</span>
          </div>
        );
      }
      if (line.trim().startsWith('#')) {
        const level = line.match(/^#+/)?.[0].length || 1;
        const text = line.replace(/^#+\s*/, '');
        const className =
          level === 1
            ? 'text-2xl font-bold mt-4 mb-2'
            : level === 2
              ? 'text-xl font-bold mt-3 mb-2'
              : 'text-lg font-semibold mt-2 mb-1';
        return (
          <div key={index} className={className}>
            {renderInlineMarkdown(text)}
          </div>
        );
      }
      if (line.trim()) {
        return (
          <p key={index} className="mb-2 text-gray-800 leading-relaxed">
            {renderInlineMarkdown(line)}
          </p>
        );
      }
      return <br key={index} />;
    });
  };

  const renderMessage = (message) => {
    const isUser = message.role === 'user';
    const feedback = messageFeedback[message.id];
    const isCopied = copiedMessageId === message.id;

    return (
      <div key={message.id} className={`group w-full ${isUser ? 'bg-white' : 'bg-gray-50'}`}>
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex gap-4">
            <div
              className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                isUser ? 'bg-blue-600' : 'bg-gradient-to-br from-blue-500 to-purple-600'
              }`}
            >
              {isUser ? <User className="w-5 h-5 text-white" /> : <Sparkles className="w-5 h-5 text-white" />}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold text-gray-900">{isUser ? 'You' : 'Kai'}</span>
                <span className="text-xs text-gray-500">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>

              <div className="prose prose-sm max-w-none">
                {message.isStreaming ? (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Kai is thinking...</span>
                  </div>
                ) : message.error ? (
                  <div className="text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{message.content}</div>
                ) : (
                  <div className="text-gray-800">{formatMessage(message.content)}</div>
                )}
              </div>

              {!message.isStreaming && !message.error && message.metadata?.chart && (
                <ChatTrendChart chart={message.metadata.chart} />
              )}

              {!message.isStreaming && !message.error && !isUser && (
                <div className="flex items-center gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => copyMessage(message.id, message.content)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Copy"
                  >
                    {isCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => regenerateResponse(message.id)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Regenerate"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleFeedback(message.id, 'thumbs_up')}
                    className={`p-1.5 rounded transition-colors ${
                      feedback === 'thumbs_up'
                        ? 'text-green-600 bg-green-50'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                    }`}
                    title="Helpful"
                  >
                    <ThumbsUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleFeedback(message.id, 'thumbs_down')}
                    className={`p-1.5 rounded transition-colors ${
                      feedback === 'thumbs_down'
                        ? 'text-red-600 bg-red-50'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                    }`}
                    title="Not helpful"
                  >
                    <ThumbsDown className="w-4 h-4" />
                  </button>
                </div>
              )}

              {message.metadata && !message.isStreaming && (message.metadata.model || message.metadata.processingTime) && (
                <div className="mt-2 text-xs text-gray-500 flex items-center gap-3">
                  {message.metadata.model && <span>{message.metadata.model}</span>}
                  {message.metadata.model && message.metadata.processingTime && <span>•</span>}
                  {message.metadata.processingTime && message.metadata.processingTime !== '—' && <span>{message.metadata.processingTime}</span>}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const filteredConversations = useMemo(() => {
    if (!searchQuery) return conversations;
    return conversations.filter((conv) => conv.title?.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [conversations, searchQuery]);

  return (
    <div className="flex min-h-[600px] h-[calc(100vh-14rem)] bg-white overflow-hidden rounded-lg border border-gray-200">
      <div
        className={`${
          sidebarOpen ? 'w-64' : 'w-0'
        } transition-all duration-300 ease-in-out border-r border-gray-200 bg-gray-50 flex flex-col overflow-hidden`}
      >
        {sidebarOpen && (
          <>
            <div className="p-4 border-b border-gray-200">
              <button
                onClick={clearConversation}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
              >
                <Plus className="w-4 h-4" />
                New Chat
              </button>
            </div>

            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {conversationsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center text-gray-500 text-sm py-8">No conversations yet</div>
              ) : (
                filteredConversations.map((conv) => (
                  <div
                    key={conv.id}
                    className={`group/conv w-full text-left p-3 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer ${
                      activeConversationId === conv.id ? 'bg-gray-100' : ''
                    }`}
                    onClick={() => loadConversation(conv.id)}
                  >
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-700 truncate flex-1">{conv.title || 'New Conversation'}</span>
                      <button
                        onClick={(e) => deleteConversation(conv.id, e)}
                        className="p-1 text-gray-400 hover:text-red-500 rounded transition-all"
                        title="Delete conversation"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Settings className="w-4 h-4" />
                Settings
              </button>
            </div>
          </>
        )}
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <History className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm text-gray-600">Kai is online</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportConversation}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Export conversation"
            >
              <Download className="w-5 h-5" />
            </button>
            {config.analyticsPath && (
              <button
                onClick={() => navigate(config.analyticsPath)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Kai Analytics"
              >
                <BarChart3 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>

        {showSettings && (
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <div className="max-w-3xl mx-auto">
              <div className="w-full md:w-64">
                <label className="block text-xs font-medium text-gray-700 mb-1">Model</label>
                <select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
                >
                  <option value={config.modelLabel}>{config.modelLabel}</option>
                </select>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {messages.map(renderMessage)}
          <div ref={messagesEndRef} />
        </div>

        <div className="border-t border-gray-200 bg-white">
          <div className="max-w-3xl mx-auto px-4 py-4">
            <div className="relative flex items-end gap-2">
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Message Kai..."
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm bg-white shadow-sm"
                  rows="1"
                  disabled={isLoading}
                  style={{ minHeight: '52px', maxHeight: '200px', overflowY: 'auto' }}
                />
              </div>
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                title="Send message"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-gray-500 px-1">
              <span>Kai can make mistakes. Check important info.</span>
              <div className="flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                <span>Powered by AgenticPractice 1.0</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernChatInterface;
